import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Paper } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
// cmp
import Na from "../na";
// hooks
import useSend from "../../hooks/useSend";
// services
import Gupport from "../../services/gupport";
import Constants from "../../services/constants";
// types
import type { GridColDef } from "@mui/x-data-grid";
import type { CmdCloudActionCloud2CloudRefreshDevices, CmdGetUserC2C, OAuth2Token } from "../../types/gupport";
import type { UserId } from "../../types/user";

type Props = Readonly<{
	userId: UserId;
}>;

const UserOAuth2Tokens = ({ userId }: Props) => {
	const send = useSend();
	const { t } = useTranslation();

	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [tokens, setTokens] = useState<ReadonlyArray<OAuth2Token>>([]);

	useEffect(() => {
		if (Gupport.getUserC2C) {
			const cmd = {
				action: "getUserC2C",
				userId: userId,
			} as const satisfies CmdGetUserC2C;
			send(cmd, (_error, msg) => {
				if (!_error && msg?.payload.status === "ok") {
					setError(null);
					setTokens(msg.payload.data);
				} else {
					setError(_error?.message ?? msg.payload.data);
					setTokens([]);
				}
				setLoading(false);
			});
		} else {
			setLoading(false);
			setError(t("users.lError"));
			setTokens([]);
		}
	}, [userId, send, t]);

	const handleModuleRefresh = useCallback((moduleId: string) => {
		setRefreshing(true);
		const cmd = {
			action: "cloudAction",
			module: "cloud2cloud",
			function: "refresh_devices",
			// TODO: should `param` be `[{...}]`?
			params: {
				id: moduleId,
			},
		} as const satisfies CmdCloudActionCloud2CloudRefreshDevices;
		send(cmd, (/*error, msg*/) => {
			setRefreshing(false);
		});
	}, [send]);

	const columns = useMemo(() => ([
		{
			field: "id",
			headerName: t("users.oauth2tokens.columns.id.title"),
			description: t("users.oauth2tokens.columns.id.tooltip"),
			flex: 3,
		},
		{
			field: "integration_id",
			headerName: t("users.oauth2tokens.columns.integration_id.title"),
			description: t("users.oauth2tokens.columns.integration_id.tooltip"),
			flex: 3,
		},
		{
			field: "expires_at",
			headerName: t("users.oauth2tokens.columns.expires_at.title"),
			description: t("users.oauth2tokens.columns.expires_at.tooltip"),
			flex: 2,
			renderCell: (params) => (<time dateTime={new Date(params.row.expires_at * 1000).toISOString()}>{new Date(params.row.expires_at * 1000).toLocaleString()}</time>),
		},
		{
			field: "expires_in",
			headerName: t("users.oauth2tokens.columns.expires_in.title"),
			description: t("users.oauth2tokens.columns.expires_in.tooltip"),
			type: "number",
			flex: 1,
		},
		{
			field: "link_ts",
			headerName: t("users.oauth2tokens.columns.link_ts.title"),
			description: t("users.oauth2tokens.columns.link_ts.tooltip"),
			flex: 2,
			renderCell: (params) => (params.row.link_ts ?? <Na />),
		},
		{
			field: "out_of_sync",
			headerName: t("users.oauth2tokens.columns.out_of_sync.title"),
			description: t("users.oauth2tokens.columns.out_of_sync.tooltip"),
			flex: 1,
		},
		{
			field: "type",
			headerName: t("users.oauth2tokens.columns.type.title"),
			description: t("users.oauth2tokens.columns.type.tooltip"),
			type: "singleSelect",
			valueOptions: Object.values(Constants.GatewayModules),
			flex: 1,
		},
		{
			field: "gateways",
			headerName: t("users.oauth2tokens.columns.gateways.title"),
			description: t("users.oauth2tokens.columns.gateways.tooltip"),
			flex: 3,
			valueFormatter: (value) => (value.join(", ")),
		},
		{
			field: "actions",
			headerName: t("users.oauth2tokens.columns.actions.title"),
			description: t("users.oauth2tokens.columns.actions.tooltip"),
			flex: 2,
			renderCell: (params) => (
				(params.row.type === Constants.GatewayModules.tuya) ?
					<Button
						loading={refreshing}
						onClick={() => (handleModuleRefresh(params.row.id))}
					>
						{t("users.oauth2tokens.columns.actions.refresh")}
					</Button>
					:
					null
			),
		},
	] as const satisfies ReadonlyArray<GridColDef<OAuth2Token>>), [refreshing, handleModuleRefresh, t]);

	if (error) {
		return <div>{error}</div>;
	}

	return (
		<Paper>
			<DataGrid
				loading={loading}
				columns={columns}
				rows={tokens}
			/>
		</Paper>
	);
};

export default UserOAuth2Tokens;
